import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layouts/MainLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { offboardingAPI, firestoreAPI, collections } from '../lib/firestore';
import { where, orderBy } from 'firebase/firestore';
import { useToast } from '../components/ui/use-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { OffboardingRequest, Notification } from '../types';
import { formatDate } from '../lib/utils';
import { CheckCircle, XCircle, Clock, AlertTriangle, User, Mail, Calendar, Send, Bell } from 'lucide-react';

export const OffboardingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [offboarding, setOffboarding] = useState<OffboardingRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [emailHistory, setEmailHistory] = useState<Notification[]>([]);

  useEffect(() => {
    if (id) {
      loadOffboarding(id);
      loadEmailHistory(id);
    }
  }, [id]);

  const loadOffboarding = async (offboardingId: string) => {
    try {
      setLoading(true);
      const data = await offboardingAPI.getById(offboardingId);
      setOffboarding(data);
    } catch (error) {
      console.error('Error loading offboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmailHistory = async (offboardingId: string) => {
    try {
      const notifications = await firestoreAPI.getAll<Notification>(
        collections.notifications,
        [
          where('relatedResourceId', '==', offboardingId),
          orderBy('sentAt', 'desc'),
        ]
      );
      setEmailHistory(notifications);
    } catch (error) {
      console.error('Error loading email history:', error);
    }
  };

  const handleSendEmails = async () => {
    if (!offboarding) return;

    try {
      setSendingEmails(true);
      const functions = getFunctions();
      const sendOffboardingEmails = httpsCallable(functions, 'sendOffboardingEmails');

      await sendOffboardingEmails({ offboardingId: offboarding.id });

      toast({
        title: 'Success',
        description: 'Notification emails sent to all admins',
        className: 'bg-green-50 border-green-200',
      });

      if (id) loadEmailHistory(id);
    } catch (error) {
      console.error('Error sending emails:', error);
      toast({
        title: 'Error',
        description: 'Failed to send emails',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setSendingEmails(false);
    }
  };

  const handleSendReminders = async () => {
    if (!offboarding) return;

    try {
      setSendingReminders(true);
      const functions = getFunctions();
      const sendReminderEmails = httpsCallable(functions, 'sendReminderEmails');

      await sendReminderEmails({ offboardingId: offboarding.id });

      toast({
        title: 'Success',
        description: 'Reminder emails sent to pending admins',
        className: 'bg-green-50 border-green-200',
      });

      if (id) loadEmailHistory(id);
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast({
        title: 'Error',
        description: 'Failed to send reminders',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setSendingReminders(false);
    }
  };

  const calculateProgress = () => {
    if (!offboarding || offboarding.applications.length === 0) return 0;

    let totalItems = 0;
    let completedItems = 0;

    offboarding.applications.forEach((app) => {
      // Only count apps with admins assigned
      if (app.adminEmails.length > 0) {
        app.adminEmails.forEach((email) => {
          totalItems++;
          if (
            app.confirmationStatus[email]?.status &&
            app.confirmationStatus[email].status !== 'pending'
          ) {
            completedItems++;
          }
        });
      }
    });

    if (totalItems === 0) return 0;
    return Math.round((completedItems / totalItems) * 100);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'deactivated':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'still_active':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'not_applicable':
        return <AlertTriangle className="h-4 w-4 text-gray-600" />;
      default:
        return <Clock className="h-4 w-4 text-orange-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'deactivated':
        return (
          <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/30">
            Deactivated
          </Badge>
        );
      case 'still_active':
        return (
          <Badge className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/30">
            Still Active
          </Badge>
        );
      case 'not_applicable':
        return (
          <Badge className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800">
            Not Applicable
          </Badge>
        );
      default:
        return (
          <Badge className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 hover:bg-orange-100 dark:hover:bg-orange-900/30">
            Pending
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading offboarding details...</div>
        </div>
      </MainLayout>
    );
  }

  if (!offboarding) {
    return (
      <MainLayout>
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30">
          <CardContent className="pt-6">
            <p className="text-red-800 dark:text-red-200">Offboarding not found</p>
            <Button className="mt-4" onClick={() => navigate('/offboarding')}>
              Back to Offboardings
            </Button>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  const progress = calculateProgress();

  // Calculate total confirmations (only count apps with admins)
  const totalConfirmations = offboarding.applications.reduce(
    (sum, app) => sum + app.adminEmails.length,
    0
  );

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/offboarding')}
              >
                ← Back
              </Button>
            </div>
            <h1 className="text-3xl font-bold mb-2">Offboarding Details</h1>
            <div className="flex items-center gap-3 text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{offboarding.employeeName}</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <span>{offboarding.employeeEmail}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {offboarding.status !== 'completed' && (
              <>
                <Button
                  variant="outline"
                  onClick={handleSendReminders}
                  disabled={sendingReminders}
                >
                  <Bell className="h-4 w-4 mr-2" />
                  {sendingReminders ? 'Sending...' : 'Send Reminders'}
                </Button>
                <Button
                  onClick={handleSendEmails}
                  disabled={sendingEmails}
                  className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sendingEmails ? 'Sending...' : 'Send Emails'}
                </Button>
              </>
            )}
            <Badge
              className={
                offboarding.status === 'completed'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/30 text-lg px-4 py-2'
                  : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 text-lg px-4 py-2'
              }
            >
              {offboarding.status === 'completed' ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Completed
                </>
              ) : (
                <>
                  <Clock className="h-4 w-4 mr-2" />
                  In Progress
                </>
              )}
            </Badge>
          </div>
        </div>

        {/* Progress Overview */}
        <Card className="border-indigo-200 dark:border-indigo-800 bg-gradient-to-r from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-900/30">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Overall Progress</h3>
                  <p className="text-sm text-muted-foreground">
                    {totalConfirmations} total confirmation{totalConfirmations !== 1 ? 's' : ''} required
                  </p>
                </div>
                <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{progress}%</div>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-indigo-600 to-indigo-700 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Metadata Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Created</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">
                {formatDate(offboarding.createdAt)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                By {offboarding.initiatedBy}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Applications</CardTitle>
              <User className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">{offboarding.applications.length}</p>
              <p className="text-xs text-muted-foreground mt-1">
                To review
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Status</CardTitle>
              {offboarding.status === 'completed' ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <Clock className="h-4 w-4 text-indigo-600" />
              )}
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold capitalize">
                {offboarding.status.replace('_', ' ')}
              </p>
              {offboarding.status === 'completed' && (
                <p className="text-xs text-muted-foreground mt-1">
                  All confirmations received
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Applications & Confirmations */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Applications & Confirmations</h2>

          {offboarding.applications.map((app) => {
            const appProgress = app.adminEmails.length > 0
              ? Math.round(
                  (app.adminEmails.filter(
                    (email) =>
                      app.confirmationStatus[email]?.status &&
                      app.confirmationStatus[email].status !== 'pending'
                  ).length /
                    app.adminEmails.length) *
                    100
                )
              : 0;

            return (
              <Card
                key={app.appId}
                className={`transition-all ${
                  appProgress === 100
                    ? 'border-green-200 dark:border-green-800 bg-green-50/30 dark:bg-green-950/20'
                    : app.adminEmails.length === 0
                    ? 'border-yellow-200 dark:border-yellow-800 bg-yellow-50/30 dark:bg-yellow-950/20'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                        <User className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{app.appName}</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {app.adminEmails.length} admin{app.adminEmails.length !== 1 ? 's' : ''} assigned
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {appProgress === 100 ? (
                        <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/30">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Complete
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-indigo-600 dark:text-indigo-400 border-indigo-600 dark:border-indigo-400">
                          {appProgress}%
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {app.adminEmails.length === 0 ? (
                    <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-800 dark:text-yellow-200">No admin assigned</p>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                          This application has no administrators to confirm the deactivation.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Progress bar for this app */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                          <span>Confirmations</span>
                          <span>{appProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-indigo-600 dark:bg-indigo-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${appProgress}%` }}
                          />
                        </div>
                      </div>

                      {/* Admin confirmations */}
                      <div className="grid gap-3">
                        {app.adminEmails.map((email) => {
                          const confirmation = app.confirmationStatus[email];
                          const status = confirmation?.status || 'pending';

                          return (
                            <div
                              key={email}
                              className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                                status === 'deactivated'
                                  ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800'
                                  : status === 'still_active'
                                  ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'
                                  : status === 'not_applicable'
                                  ? 'bg-muted/50 border-gray-200 dark:border-gray-700'
                                  : 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {getStatusIcon(status)}
                                <div>
                                  <p className="font-medium">{email}</p>
                                  {confirmation?.confirmedAt && (
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      Confirmed on {formatDate(confirmation.confirmedAt)}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {getStatusBadge(status)}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Email History */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Email History</h2>
          {emailHistory.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground text-center">No emails sent yet.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {emailHistory.map((notification) => (
                    <div
                      key={notification.id}
                      className="flex items-center justify-between p-4 rounded-lg border bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                        <div>
                          <p className="font-medium">{notification.recipientEmail}</p>
                          <p className="text-sm text-muted-foreground">
                            {notification.type === 'offboarding_email' ? 'Notification' : 'Reminder'}
                            {notification.triggeredBy && (
                              <> — Sent by {notification.triggeredBy}</>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge
                          className={
                            notification.status === 'sent'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/30'
                              : notification.status === 'failed'
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/30'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                          }
                        >
                          {notification.status}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(notification.sentAt)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </MainLayout>
  );
};
